import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import Table from "@mui/joy/Table";
import ToggleButtonGroup from "@mui/joy/ToggleButtonGroup";
import Button from "@mui/joy/Button";
import { BarChart } from "@mui/x-charts/BarChart";
import { PieChart } from "@mui/x-charts/PieChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../components/AnalyticsShared";
import { useAsyncBlock } from "../hooks";
import { bidsSaleChannelsApi } from "../../bidsApi";
import { BidSaleChannel, BidSaleChannelReport, BidSaleChannelTotal } from "../../../../models/bidAnalytics";
import { formatInt, formatMoneyKopecks, formatRatePercent, toNum } from "../format";
import { formatBucketLabel, rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";

type Mode = "count" | "sum";
type StatusFilter = "completed" | "active";

/** Порядок каналов в легендах и таблице: быстрая продажа первой */
const CHANNELS: Array<{ channel: BidSaleChannel; key: string; label: string; color: string }> = [
    { channel: BidSaleChannel.QuickSale, key: "quickSale", label: "Быстрая продажа", color: "#e65100" },
    { channel: BidSaleChannel.ProductPage, key: "productPage", label: "Страница товара", color: "#1976d2" },
    { channel: BidSaleChannel.AutoMatch, key: "autoMatch", label: "Авто-матчинг", color: "#2e7d32" },
    { channel: BidSaleChannel.ExternalApi, key: "externalApi", label: "External API", color: "#6a1b9a" },
    { channel: BidSaleChannel.Unknown, key: "unknown", label: "Без разметки", color: "#9e9e9e" },
];

/** Офлайн-покупки — срез поверх каналов: отдельная серия вне стека, в пироги и «Итого» не входит */
const OFFLINE = { key: "offline", label: "Офлайн-покупки", color: "#00838f" };

const itemsKey = (key: string) => `${key}Items`;
const sumKey = (key: string) => `${key}Sum`;

const formatRub = (rubles: number) => `${rubles.toLocaleString("ru-RU", { maximumFractionDigits: 0 })} ₽`;

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function SaleChannelsTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;
    const [mode, setMode] = React.useState<Mode>("sum");
    const [status, setStatus] = React.useState<StatusFilter>("completed");

    const [state, refresh] = useAsyncBlock<BidSaleChannelReport>(
        () =>
            bidsSaleChannelsApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                onlyCompleted: status === "completed",
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, status]
    );

    const totalsByChannel = React.useMemo(() => {
        const map = new Map<BidSaleChannel, BidSaleChannelTotal>();
        for (const c of state.data?.channels ?? []) {
            map.set(c.channel, c);
        }
        return map;
    }, [state.data?.channels]);

    // Каналы, по которым есть продажи за период — только они попадают в графики и таблицу
    const presentChannels = React.useMemo(
        () => CHANNELS.filter((c) => totalsByChannel.has(c.channel)),
        [totalsByChannel]
    );

    const grand = React.useMemo(() => {
        let orders = 0;
        let items = 0;
        let sum = 0;
        for (const c of state.data?.channels ?? []) {
            orders += c.ordersCount;
            items += c.itemsCount;
            sum += toNum(c.sum);
        }
        return { orders, items, sum };
    }, [state.data?.channels]);

    const chartData = React.useMemo(() => {
        // Плоский ряд (час, канал) + ряд офлайн-покупок → одна строка на час с колонками по каналам и срезу
        const seriesKeys = [...CHANNELS.map((c) => c.key), OFFLINE.key];
        const byHour = new Map<string, Record<string, any> & { hour: string }>();
        const rowFor = (hour: string) => {
            let row = byHour.get(hour);
            if (!row) {
                row = { hour };
                for (const key of seriesKeys) {
                    row[itemsKey(key)] = 0;
                    row[sumKey(key)] = 0;
                }
                byHour.set(hour, row);
            }
            return row;
        };

        for (const p of state.data?.series ?? []) {
            const meta = CHANNELS.find((c) => c.channel === p.channel);
            if (!meta) {
                continue;
            }
            const row = rowFor(p.hour);
            row[itemsKey(meta.key)] += p.itemsCount;
            row[sumKey(meta.key)] += toNum(p.sum);
        }
        for (const p of state.data?.offlineSeries ?? []) {
            const row = rowFor(p.hour);
            row[itemsKey(OFFLINE.key)] += p.itemsCount;
            row[sumKey(OFFLINE.key)] += toNum(p.sum);
        }

        const rolled = rollupSeries(Array.from(byHour.values()), filters.granularity, {
            sumKeys: seriesKeys.flatMap((key) => [itemsKey(key), sumKey(key)]),
        });
        return rolled.map((row) => {
            const point: Record<string, any> & { x: Date } = { x: row.x };
            for (const key of seriesKeys) {
                point[key] = mode === "count" ? toNum(row[itemsKey(key)]) : toNum(row[sumKey(key)]) / 100;
            }
            return point;
        });
    }, [state.data?.series, state.data?.offlineSeries, filters.granularity, mode]);

    const valueFmt = (v: number | null) => {
        if (v == null) {
            return "—";
        }
        return mode === "count" ? formatInt(v) : formatRub(v);
    };

    const pieData = (by: Mode) =>
        presentChannels.map((c) => {
            const total = totalsByChannel.get(c.channel)!;
            return {
                id: c.key,
                label: c.label,
                color: c.color,
                value: by === "count" ? total.itemsCount : toNum(total.sum) / 100,
                share: by === "count" ? total.itemsShare : total.sumShare,
            };
        });

    const quickSale = totalsByChannel.get(BidSaleChannel.QuickSale);
    const offline = state.data?.offline;
    const hasOffline = (offline?.ordersCount ?? 0) > 0;
    const hasOfflineSeries = hasOffline && (state.data?.offlineSeries?.length ?? 0) > 0;
    // Старый бэк не присылает срез — показываем «—», а не 0, чтобы не путать с «офлайн-покупок не было»
    const offlineBuyers = state.data?.offlineBuyersCount;
    const isEmpty = state.data != null && grand.items === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Через что продают по заявкам"
                subtitle="Проданные по заявкам скины и суммы в разрезе каналов: быстрая продажа, страница товара, авто-матчинг, External API. Отдельно — офлайн-покупки."
                snapshotLabel="period"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>
                            <Typography level="body-sm">
                                Считаются заказы по заявкам, созданные в периоде (бакет — дата создания заказа).
                                Количество — проданные скины (позиции заказов), сумма — цена покупки в ₽.
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Только успешные</strong> — завершённые заказы.{" "}
                                <strong>Все, кроме провальных</strong> — ещё и заказы в процессе передачи.
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Быстрая продажа</strong> — массовая продажа со страницы «Быстрая продажа»;{" "}
                                <strong>Страница товара</strong> — продажа своего скина по выбранной заявке на странице товара;{" "}
                                <strong>Авто-матчинг</strong> — выставленный скин сам сматчился с заявкой.
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Без разметки</strong> — заказы, созданные до появления учёта каналов.
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Офлайн-покупки</strong> — заказы по офлайн-заявкам: покупатель был не в сети,
                                а офлайн-заявки включены глобально и у него в настройках. Это срез поверх каналов:
                                такой заказ уже учтён в своём канале, поэтому срез не входит в «Итого» и круговые
                                диаграммы. Заказы до появления учёта офлайн-заявок не размечены.
                            </Typography>
                        </ListItem>
                    </List>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Доля быстрой продажи по сумме",
                                ifGrows: "продавцы чаще закрывают заявки массово — функционал востребован.",
                                ifFalls: "быстрой продажей пользуются реже; проверьте страницу и цены заявок.",
                            },
                            {
                                signal: "Доля авто-матчинга",
                                ifGrows: "больше сделок проходит без участия продавца.",
                                ifFalls: "выставленные скины реже совпадают с заявками по цене.",
                            },
                        ]}
                    />
                }
            >
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <ToggleButtonGroup
                        size="sm"
                        value={status}
                        onChange={(_, v) => v && setStatus(v as StatusFilter)}
                    >
                        <Button value="completed">Только успешные</Button>
                        <Button value="active">Все, кроме провальных</Button>
                    </ToggleButtonGroup>
                    <ToggleButtonGroup size="sm" value={mode} onChange={(_, v) => v && setMode(v as Mode)}>
                        <Button value="sum">Сумма ₽</Button>
                        <Button value="count">Количество</Button>
                    </ToggleButtonGroup>
                </Box>

                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && isEmpty && <EmptyState />}
                {!state.loading && !state.error && state.data && !isEmpty && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <MetricHint
                                label="Быстрая продажа: скинов"
                                value={formatInt(quickSale?.itemsCount ?? 0)}
                                hint={`заказов: ${formatInt(quickSale?.ordersCount ?? 0)}`}
                                color="warning"
                            />
                            <MetricHint
                                label="Быстрая продажа: сумма"
                                value={formatMoneyKopecks(quickSale?.sum ?? 0)}
                                color="warning"
                            />
                            <MetricHint
                                label="Доля по количеству"
                                value={formatRatePercent(quickSale?.itemsShare ?? 0)}
                            />
                            <MetricHint label="Доля по сумме" value={formatRatePercent(quickSale?.sumShare ?? 0)} />
                            <MetricHint
                                label="Всего по заявкам"
                                value={formatMoneyKopecks(grand.sum)}
                                hint={`скинов: ${formatInt(grand.items)}`}
                            />
                            <MetricHint
                                label="Юзеров с офлайн-покупками"
                                value={offlineBuyers == null ? "—" : formatInt(offlineBuyers)}
                                hint={offline ? `заказов: ${formatInt(offline.ordersCount)}` : undefined}
                            />
                        </Box>

                        <Typography level="title-sm">Доли каналов</Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                            {(["count", "sum"] as Mode[]).map((by) => {
                                const data = pieData(by);
                                return (
                                    <Box
                                        key={by}
                                        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                                    >
                                        <Typography level="body-sm" fontWeight="lg">
                                            {by === "count" ? "По количеству" : "По сумме"}
                                        </Typography>
                                        <PieChart
                                            series={[
                                                {
                                                    data,
                                                    innerRadius: 50,
                                                    outerRadius: 100,
                                                    paddingAngle: 2,
                                                    cornerRadius: 4,
                                                    valueFormatter: (item) => {
                                                        const share = data.find((d) => d.id === item.id)?.share;
                                                        const value =
                                                            by === "count" ? formatInt(item.value) : formatRub(item.value);
                                                        return `${value} (${formatRatePercent(share)})`;
                                                    },
                                                },
                                            ]}
                                            width={340}
                                            height={280}
                                            legend={{
                                                direction: "row",
                                                position: { vertical: "bottom", horizontal: "middle" },
                                            }}
                                            margin={{ top: 10, bottom: 80, left: 10, right: 10 }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Typography level="title-sm">
                            Динамика по каналам ({mode === "count" ? "скины" : "₽"})
                        </Typography>
                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                            <Box sx={{ minWidth: Math.max(480, chartData.length * 24) }}>
                                <BarChart
                                    height={300}
                                    dataset={chartData}
                                    xAxis={[
                                        {
                                            dataKey: "x",
                                            scaleType: "band",
                                            valueFormatter: (v: Date) => formatBucketLabel(v, filters.granularity),
                                        },
                                    ]}
                                    series={[
                                        ...presentChannels.map((c) => ({
                                            dataKey: c.key,
                                            label: c.label,
                                            color: c.color,
                                            stack: "total",
                                            valueFormatter: valueFmt,
                                        })),
                                        // Офлайн-покупки пересекаются с каналами — отдельным столбцом, не в стеке
                                        ...(hasOfflineSeries
                                            ? [
                                                  {
                                                      dataKey: OFFLINE.key,
                                                      label: OFFLINE.label,
                                                      color: OFFLINE.color,
                                                      valueFormatter: valueFmt,
                                                  },
                                              ]
                                            : []),
                                    ]}
                                    margin={{ left: 70, right: 20, top: 40, bottom: 40 }}
                                />
                            </Box>
                        </Box>

                        <ScrollTable>
                            <Table size="sm" stickyHeader sx={{ minWidth: 640 }}>
                                <thead>
                                    <tr>
                                        <th>Канал</th>
                                        <th>Заказов</th>
                                        <th>Скинов</th>
                                        <th>Доля шт.</th>
                                        <th>Сумма</th>
                                        <th>Доля ₽</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {presentChannels.map((c) => {
                                        const t = totalsByChannel.get(c.channel)!;
                                        return (
                                            <tr key={c.key}>
                                                <td>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Box
                                                            sx={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius: "50%",
                                                                bgcolor: c.color,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        {c.label}
                                                    </Box>
                                                </td>
                                                <td>{formatInt(t.ordersCount)}</td>
                                                <td>{formatInt(t.itemsCount)}</td>
                                                <td>{formatRatePercent(t.itemsShare)}</td>
                                                <td>{formatMoneyKopecks(t.sum)}</td>
                                                <td>{formatRatePercent(t.sumShare)}</td>
                                            </tr>
                                        );
                                    })}
                                    <tr>
                                        <td>
                                            <strong>Итого</strong>
                                        </td>
                                        <td>{formatInt(grand.orders)}</td>
                                        <td>{formatInt(grand.items)}</td>
                                        <td>100%</td>
                                        <td>{formatMoneyKopecks(grand.sum)}</td>
                                        <td>100%</td>
                                    </tr>
                                    {offline && hasOffline && (
                                        <Box component="tr" sx={{ color: "text.tertiary" }}>
                                            <td>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 10,
                                                            height: 10,
                                                            borderRadius: "50%",
                                                            bgcolor: OFFLINE.color,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <Typography level="body-sm" fontStyle="italic" textColor="inherit">
                                                        в т.ч. офлайн-покупки
                                                    </Typography>
                                                </Box>
                                            </td>
                                            <td>{formatInt(offline.ordersCount)}</td>
                                            <td>{formatInt(offline.itemsCount)}</td>
                                            <td>{formatRatePercent(offline.itemsShare)}</td>
                                            <td>{formatMoneyKopecks(offline.sum)}</td>
                                            <td>{formatRatePercent(offline.sumShare)}</td>
                                        </Box>
                                    )}
                                </tbody>
                            </Table>
                        </ScrollTable>
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
